import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Heart, X, Shield, Star, MapPin, Sparkles, MessageCircle, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export default function Swipe() {
  const navigate = useNavigate();
  const [deck, setDeck] = useState([]);
  const [i, setI] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exit, setExit] = useState(null);      // "like" | "pass" | null
  const [match, setMatch] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/deck"); setDeck(data.deck); setI(0); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const current = deck[i];

  const act = async (direction) => {
    if (!current || exit) return;
    setExit(direction);
    const target = current;
    setTimeout(async () => {
      setI((v) => v + 1);
      setExit(null);
      try {
        const { data } = await api.post("/swipe", { target_id: target.id, direction });
        if (data.matched && data.match_user) setMatch(data.match_user);
      } catch { /* ignore */ }
    }, 320);
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-sand flex flex-col items-center py-8 px-5">
      <div className="text-center mb-6">
        <h1 className="font-display text-4xl font-black text-charcoal flex items-center justify-center gap-2">
          <Sparkles className="w-7 h-7 text-terracotta" /> Find your Saathi
        </h1>
        <p className="text-charcoal/60 mt-1 text-sm">Swipe right to connect, left to pass. Match to chat & meet — platonically.</p>
      </div>

      <div className="relative w-full max-w-sm h-[520px]">
        {loading ? (
          <div className="absolute inset-0 bg-white rounded-3xl border border-border animate-pulse" />
        ) : !current ? (
          <div className="absolute inset-0 bg-white rounded-3xl border border-dashed border-border flex flex-col items-center justify-center text-center p-8">
            <Heart className="w-12 h-12 text-terracotta/40 mb-3" />
            <p className="font-display text-xl font-bold text-charcoal">You're all caught up!</p>
            <p className="text-sm text-charcoal/60 mt-1">Check back soon for new people, or revisit the deck.</p>
            <Button data-testid="reload-deck" onClick={load} className="mt-4 rounded-full bg-terracotta hover:bg-terracotta/90 text-white"><RotateCcw className="w-4 h-4 mr-1" /> Reload</Button>
          </div>
        ) : (
          <>
            {deck[i + 1] && <CardShell card={deck[i + 1]} className="scale-95 translate-y-3 opacity-60" />}
            <CardShell
              card={current}
              data-testid="swipe-card"
              className={`transition-all duration-300 ${
                exit === "like" ? "translate-x-[140%] rotate-12 opacity-0" :
                exit === "pass" ? "-translate-x-[140%] -rotate-12 opacity-0" : ""
              }`}
            />
          </>
        )}
      </div>

      {current && !loading && (
        <div className="flex items-center gap-6 mt-7">
          <button data-testid="swipe-pass" onClick={() => act("pass")}
            className="w-16 h-16 rounded-full bg-white border-2 border-border shadow-md flex items-center justify-center text-signal hover:scale-110 transition-transform duration-200">
            <X className="w-8 h-8" strokeWidth={2.5} />
          </button>
          <button data-testid="swipe-view" onClick={() => navigate(`/host/${current.id}`)}
            className="w-12 h-12 rounded-full bg-white border-2 border-border shadow flex items-center justify-center text-charcoal/60 hover:scale-110 transition-transform duration-200">
            <Star className="w-5 h-5" />
          </button>
          <button data-testid="swipe-like" onClick={() => act("like")}
            className="w-16 h-16 rounded-full bg-terracotta shadow-lg flex items-center justify-center text-white hover:scale-110 transition-transform duration-200">
            <Heart className="w-8 h-8" fill="white" strokeWidth={2} />
          </button>
        </div>
      )}

      {/* Match modal */}
      {match && (
        <div className="fixed inset-0 z-50 bg-charcoal/70 backdrop-blur-sm flex items-center justify-center px-6" data-testid="match-modal">
          <div className="bg-white rounded-3xl p-8 max-w-sm w-full text-center fade-up">
            <p className="font-display text-4xl font-black text-terracotta italic">It's a match!</p>
            <p className="text-charcoal/70 mt-2">You and {match.name} both want to connect.</p>
            <img src={match.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${match.name}`}
              alt={match.name} className="w-24 h-24 rounded-full object-cover mx-auto mt-5 border-4 border-terracotta/30" />
            <p className="font-display text-xl font-bold text-charcoal mt-3">{match.name}{match.age ? `, ${match.age}` : ""}</p>
            {match.personality_type && <p className="text-sm text-terracotta font-semibold">{match.personality_type}</p>}
            <div className="flex gap-3 mt-6">
              <Button data-testid="match-keep-swiping" variant="outline" onClick={() => setMatch(null)} className="flex-1 rounded-full border-charcoal/20">Keep swiping</Button>
              <Button data-testid="match-message" onClick={() => navigate(`/chat/${match.id}`)} className="flex-1 rounded-full bg-terracotta hover:bg-terracotta/90 text-white"><MessageCircle className="w-4 h-4 mr-1" /> Say hi</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CardShell({ card, className = "", ...rest }) {
  return (
    <div {...rest} className={`absolute inset-0 bg-white rounded-3xl border border-border overflow-hidden shadow-xl ${className}`}>
      <div className="relative h-2/3">
        <img src={card.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${card.name}`}
          alt={card.name} className="w-full h-full object-cover" />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/60 to-transparent" />
        <span className="absolute top-3 right-3 bg-sage text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1 shadow">
          <Shield className="w-3.5 h-3.5" /> {card.trust_score}%
        </span>
        <div className="absolute bottom-3 left-4 text-white">
          <h3 className="font-display text-2xl font-bold">{card.name}{card.age ? `, ${card.age}` : ""}</h3>
          <p className="text-sm flex items-center gap-1 text-white/90"><MapPin className="w-3.5 h-3.5" /> {card.city}
            {card.rating && <span className="ml-2 flex items-center gap-0.5"><Star className="w-3.5 h-3.5 fill-sun text-sun" /> {card.rating}</span>}</p>
        </div>
      </div>
      <div className="p-4 h-1/3 flex flex-col">
        {card.personality_type && (
          <span className="inline-flex items-center gap-1 text-xs font-bold text-terracotta mb-1"><Sparkles className="w-3.5 h-3.5" /> {card.personality_type}</span>
        )}
        <p className="text-sm text-charcoal/70 line-clamp-2 leading-relaxed">{card.bio || "Here for good conversation and public hangouts."}</p>
        <div className="flex flex-wrap gap-1.5 mt-auto pt-2">
          {(card.interests || []).slice(0, 3).map((it) => (
            <span key={it} className="text-xs font-semibold bg-sand text-charcoal/70 px-2.5 py-1 rounded-full">{it}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
